import React, {useState} from "react";
import {EmployeeBudget, ManagerStats} from "arteco-api-client-ts";
import {
	Box,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {EmptyState, FilterChips, ListCard, Panel, SoftChip, tableContainerSx, tableSx, Tone} from "../../../components/kit";

export interface EmpProductivityBudgetsProps {
	stats: ManagerStats;
	/** Con el filtro «Requieren acción» y por tipo, como en la visión general (#14497). */
	withFilters?: boolean;
}

type BudgetFilter = "ACTION" | "ALL" | "PROYECTO" | "BOLSA" | "CUOTA" | "TM";

const h = (n?: number | null) => n === undefined || n === null ? "—" : n.toLocaleString("es-ES", {minimumFractionDigits: 1, maximumFractionDigits: 1});
const pct0 = (n: number) => `${Math.round(n).toLocaleString("es-ES")} %`;

const DAY = 86400000;
/** Entrega a menos de estos días con menos de este % consumido: va tarde (#14556). */
const PUSH_DAYS = 7;
const LOW_CONSUMED_PCT = 50;
/** Consumido por encima de esto sin entregar: o se entrega o se pasa del techo. */
const HIGH_CONSUMED_PCT = 90;
/** Puntos de diferencia entre consumido y facturado a partir de los que una bolsa pide factura. */
const UNBILLED_MARGIN_PCT = 5;

/**
 * Dedicadas sobre el techo entero, en %. Solo proyectos y bolsas: T&M y cuotas no tienen techo de avance. Sobre
 * ceilingHours y no totalBudgetHours, que en un presupuesto prorrateado es solo lo de hoy.
 */
export const consumedPct = (b: EmployeeBudget): number | undefined => {
	const ceiling = b.ceilingHours ?? b.totalBudgetHours ?? 0;
	return (b.kind === "PROYECTO" || b.kind === "BOLSA") && ceiling > 0
		? (b.dedicatedHours || 0) / ceiling * 100
		: undefined;
};

/** Días hasta la entrega comprometida (negativo si ya pasó); undefined si no hay fecha o ya se entregó. */
export const daysToDelivery = (b: EmployeeBudget, today = new Date()): number | undefined => {
	if (!b.committedDeliveryDate || b.deliveredDate) {
		return undefined;
	}
	const t = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
	return Math.round((new Date(b.committedDeliveryDate + "T00:00:00").getTime() - t) / DAY);
};

export type PushCode = "LATE" | "SOON" | "HIGH" | "UNBILLED";

export interface PushReason {
	code: PushCode;
	tone: Tone;
	text: string;
}

/**
 * Dónde empujar (#14556): entrega vencida, o cerca con poco consumido; casi todo consumido sin entregar; y en las
 * bolsas, consumido sin facturar. El % de horas no es avance del alcance, pero es lo que hay.
 */
export const pushReasons = (b: EmployeeBudget, today = new Date()): PushReason[] => {
	const reasons: PushReason[] = [];
	const consumed = consumedPct(b);
	const days = daysToDelivery(b, today);
	const consumedText = consumed === undefined ? "" : ` con el ${pct0(consumed)} consumido`;
	if (days !== undefined && days < 0) {
		reasons.push({code: "LATE", tone: "error", text: `Entrega vencida hace ${-days} ${days === -1 ? "día" : "días"}${consumedText}`});
	} else if (days !== undefined && days < PUSH_DAYS && consumed !== undefined && consumed < LOW_CONSUMED_PCT) {
		reasons.push({code: "SOON", tone: "warning", text: `Entrega ${days === 0 ? "hoy" : `en ${days} ${days === 1 ? "día" : "días"}`}${consumedText}`});
	}
	// Una bolsa no se «entrega»: se agota aunque tenga fecha de entrega.
	if (consumed !== undefined && consumed > HIGH_CONSUMED_PCT && (b.kind === "BOLSA" || !b.deliveredDate)) {
		reasons.push({
			code: "HIGH", tone: "warning",
			text: b.kind === "BOLSA" ? `Bolsa al ${pct0(consumed)}, se agota` : `${pct0(consumed)} consumido sin entregar`,
		});
	}
	if (b.kind === "BOLSA" && consumed !== undefined && b.billedPct != null && consumed > b.billedPct + UNBILLED_MARGIN_PCT) {
		reasons.push({code: "UNBILLED", tone: "warning", text: `Toca facturar: ${pct0(consumed)} consumido, ${pct0(b.billedPct)} facturado`});
	}
	return reasons;
};

/** Exceso de horas o de cuota, aceptación o análisis pendientes, o algo que empujar (#14556). */
export const requiresAction = (b: EmployeeBudget) =>
	(b.deviationHours || 0) > 0 || !b.acceptation || !b.analysis
	|| (b.kind === "CUOTA" && (b.plannedHours || 0) > 0 && (b.dedicatedHours || 0) > (b.plannedHours || 0))
	|| pushReasons(b).length > 0;

/** Consumido con barra; en rojo si pasa del techo, en ámbar si va por delante de lo facturado (#14556). */
const Consumed = ({b}: { b: EmployeeBudget }) => {
	const consumed = consumedPct(b);
	if (consumed === undefined) {
		return <Typography variant="body2" component="span" color="text.disabled">—</Typography>;
	}
	const ahead = b.billedPct != null && consumed > b.billedPct + UNBILLED_MARGIN_PCT;
	const color = consumed > 100 ? "error" : ahead ? "warning" : "primary";
	return (
		<Box sx={{minWidth: 84}}>
			<Typography variant="body2" component="span" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}
						color={consumed > 100 ? "error.main" : undefined}>{pct0(consumed)}</Typography>
			<LinearProgress variant="determinate" value={Math.min(consumed, 100)} color={color}
							aria-label={`${pct0(consumed)} consumido`}
							sx={{mt: 0.5, height: 6, borderRadius: 3, bgcolor: "grey.200", "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
		</Box>
	);
};

/** Entrega comprometida y días que quedan; en rojo si venció sin entregar (#14556). */
const Delivery = ({b}: { b: EmployeeBudget }) => {
	if (b.deliveredDate) {
		return <>
			<Typography variant="body2" component="span" sx={{fontVariantNumeric: "tabular-nums"}}>{shortDate(b.deliveredDate)}</Typography>
			<Typography variant="caption" color="success.main" display="block">entregado</Typography>
		</>;
	}
	if (!b.committedDeliveryDate) {
		return <Typography variant="body2" component="span" color="text.disabled">—</Typography>;
	}
	const days = daysToDelivery(b)!;
	return <>
		<Typography variant="body2" component="span" sx={{fontVariantNumeric: "tabular-nums"}}
					color={days < 0 ? "error.main" : undefined} fontWeight={days < 0 ? 700 : undefined}>{shortDate(b.committedDeliveryDate)}</Typography>
		<Typography variant="caption" display="block" color={days < 0 ? "error.main" : days < PUSH_DAYS ? "warning.main" : "text.secondary"}>
			{days < 0 ? `vencida hace ${-days} d` : days === 0 ? "hoy" : `quedan ${days} d`}
		</Typography>
	</>;
};

const shortDate = (d: string) => new Date(d + "T00:00:00").toLocaleDateString("es-ES", {day: "numeric", month: "short", year: "2-digit"});

/** Las razones para empujar, debajo del título. */
const PushHints = ({b}: { b: EmployeeBudget }) => {
	const reasons = pushReasons(b);
	return reasons.length === 0 ? null : (
		<Stack spacing={0.25} sx={{mt: 0.5}}>
			{reasons.map(r => (
				<Typography key={r.code} variant="caption" fontWeight={600} color={`${r.tone}.main`} display="block">
					Empujar · {r.text}
				</Typography>
			))}
		</Stack>
	);
};

/** Desvío: positivo es exceso (rojo); negativo, margen (verde); null en T&M y cuotas (#13747). */
const Deviation = ({value, bold}: { value?: number | null, bold?: boolean }) => (
	<Typography variant="body2" component="span" fontWeight={bold ? 700 : 600}
				sx={{color: value == null ? "text.disabled" : value > 0 ? "error.main" : "success.main", fontVariantNumeric: "tabular-nums"}}>
		{value == null ? "—" : (value > 0 ? "+" : "") + h(value)}
	</Typography>
);

const eur = (n?: number | null) => n == null ? "—" : n.toLocaleString("es-ES", {style: "currency", currency: "EUR", maximumFractionDigits: 0});

const monthLabel = (d?: string) => d ? new Date(d + "T00:00:00").toLocaleDateString("es-ES", {month: "short", year: "numeric"}) : "";

/** El periodo en curso de una cuota: «oct 2026» o «ago 2026 – oct 2026» (#14462). */
const periodLabel = (b: EmployeeBudget) => {
	if (!b.periodFrom) {
		return undefined;
	}
	const from = monthLabel(b.periodFrom);
	const to = monthLabel(b.periodTo);
	return from === to ? from : `${from} – ${to}`;
};

/** Facturado: en T&M, euros (no tiene importe cerrado); en el resto, % del importe (de la cuota del periodo). */
const billedLabel = (b: EmployeeBudget) =>
	b.kind === "TM" ? eur(b.billedAmount) : b.billedPct == null ? "—" : `${h(b.billedPct)} %`;

/** Las horas cerradas ajustadas a lo facturado no son las trabajadas: se enseñan las dos (#14462). */
const WorkedHint = ({b}: { b: EmployeeBudget }) =>
	Math.abs((b.closedWorkedHours ?? b.closedHours ?? 0) - (b.closedHours ?? 0)) >= 0.05 ? (
		<Typography variant="caption" color="text.secondary" display="block">
			{h(b.closedWorkedHours)} trabajadas
		</Typography>
	) : null;

const Flag = ({ok, label}: { ok?: boolean, label: string }) =>
	<SoftChip tone={ok ? "success" : "warning"} label={ok ? label : label + " pendiente"}/>;

export const EmpProductivityBudgets = ({stats, withFilters}: EmpProductivityBudgetsProps) => {
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [filter, setFilter] = useState<BudgetFilter>(withFilters ? "ACTION" : "ALL");
	const all = stats?.budgets || [];
	if (all.length === 0) {
		return null;
	}
	const budgets = all.filter(b => filter === "ALL" ? true : filter === "ACTION" ? requiresAction(b) : b.kind === filter);
	const count = (f: BudgetFilter) => all.filter(b => f === "ALL" ? true : f === "ACTION" ? requiresAction(b) : b.kind === f).length;
	const filters = withFilters ? (
		<Box sx={{mb: 2}}>
			<FilterChips value={filter} onChange={setFilter} options={([
				{label: "Requieren acción", value: "ACTION"}, {label: "Todos", value: "ALL"}, {label: "Proyectos", value: "PROYECTO"},
				{label: "Bolsas", value: "BOLSA"}, {label: "Cuotas", value: "CUOTA"}, {label: "T&M", value: "TM"},
			] as { label: string, value: BudgetFilter }[]).map(o => ({...o, count: count(o.value)}))}/>
		</Box>
	) : null;

	// Desvío (#13747): consumido − planificado a hoy, positivo = exceso. Null en T&M y cuotas.
	const deviationTotal = budgets.reduce((acc, b: EmployeeBudget) => acc + (b.deviationHours ?? 0), 0);
	// El T&M no tiene techo y suma horas de toda su vida (Meliá desde 2018): fuera de los totales (#14462).
	const withCeiling = budgets.filter(b => b.kind !== "TM");
	const plannedTotal = withCeiling.reduce((acc, b) => acc + (b.totalBudgetHours || 0), 0);
	const dedicatedTotal = withCeiling.reduce((acc, b) => acc + (b.dedicatedHours || 0), 0);
	// Lo interno (cliente Arteco sin presupuesto) no es una hora perdida: fuera de «sin asignar» (#14556).
	const internal = stats?.dedication?.internalHours || 0;
	const unassigned = Math.max(0, (stats?.dedication?.unassignedHours || 0) - internal);

	return (
		<Panel title="Presupuestos"
			   subtitle="Los activos que tienes asignados. Indica el código de cliente y de presupuesto en las tareas de Redmine; si no lo está, se busca hacia arriba en la jerarquía.">
			{filters}
			{budgets.length === 0 ? (
				<EmptyState title="Ningún presupuesto en este filtro" description="Prueba con «Todos»."/>
			) : compact ? (
				<Stack spacing={1.5}>
					{budgets.map((b, idx) => (
						<ListCard key={idx}>
							<Typography variant="caption" color="text.secondary">{b.customerId} · {b.customerName}</Typography>
							<Typography variant="body2" fontWeight={700}>{b.id} · {b.title}</Typography>
							<PushHints b={b}/>
							{/* Igual que en la tabla: solo lo pendiente (#14556). */}
							{(!b.analysis || !b.acceptation) && (
								<Stack direction="row" spacing={1} sx={{mt: 1}} flexWrap="wrap" useFlexGap>
									{!b.analysis && <Flag label="Análisis"/>}
									{!b.acceptation && <Flag label="Aceptación"/>}
								</Stack>
							)}
							<Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5}}>
								<Box><Typography variant="caption" color="text.secondary" display="block">Planificadas</Typography><Typography variant="body2" fontWeight={600}>{h(b.totalBudgetHours)}</Typography>{periodLabel(b) && <Typography variant="caption" color="text.secondary" display="block">{periodLabel(b)}</Typography>}</Box>
								<Box><Typography variant="caption" color="text.secondary" display="block">Dedicadas</Typography><Typography variant="body2" fontWeight={600}>{h(b.dedicatedHours)}</Typography><WorkedHint b={b}/></Box>
								<Box><Typography variant="caption" color="text.secondary" display="block">Desvío</Typography><Deviation value={b.deviationHours}/></Box>
								<Box><Typography variant="caption" color="text.secondary" display="block">Consumido</Typography><Consumed b={b}/></Box>
								<Box><Typography variant="caption" color="text.secondary" display="block">Facturado</Typography><Typography variant="body2" fontWeight={600}>{billedLabel(b)}</Typography></Box>
								<Box><Typography variant="caption" color="text.secondary" display="block">Entrega</Typography><Delivery b={b}/></Box>
							</Box>
						</ListCard>
					))}
				</Stack>
			) : (
				<TableContainer sx={tableContainerSx}>
					<Table size="small" sx={tableSx}>
						<TableHead>
							<TableRow>
								{/* La tabla es de ancho fijo: sin anchos, las siete columnas salen iguales y el título se aprieta (#14556). */}
								<TableCell sx={{width: "31%"}}>Presupuesto</TableCell>
								<TableCell align="right" sx={{width: "10%"}}>Planificadas</TableCell>
								<TableCell align="right" sx={{width: "13%"}}>Dedicadas</TableCell>
								<TableCell align="right" sx={{width: "8%"}}>Desvío</TableCell>
								<TableCell sx={{width: "14%"}}>Consumido</TableCell>
								<TableCell align="right" sx={{width: "11%"}}>Facturado</TableCell>
								<TableCell sx={{width: "13%"}}>Entrega</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{budgets.map((b, idx) => (
								<TableRow key={idx} hover>
									<TableCell>
										<Typography variant="body2" fontWeight={600}>{b.id} · {b.title}</Typography>
										<Typography variant="caption" color="text.secondary">{b.customerId} · {b.customerName}</Typography>
										<PushHints b={b}/>
										{/* Estado: solo lo pendiente, para dejar sitio al consumido y la entrega (#14556). */}
										{(!b.analysis || !b.acceptation) && (
											<Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{mt: 0.75}}>
												{!b.analysis && <Flag label="Análisis"/>}
												{!b.acceptation && <Flag label="Aceptación"/>}
											</Stack>
										)}
									</TableCell>
									<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>
										{h(b.totalBudgetHours)}
										{periodLabel(b) && (
											<Typography variant="caption" color="text.secondary" display="block">{periodLabel(b)}</Typography>
										)}
									</TableCell>
									<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>
										{h(b.dedicatedHours)}
										{(b.provisionalHours || 0) > 0 && (
											<Typography variant="caption" color="text.secondary" display="block">
												incl. {h(b.provisionalHours)} provisionales
											</Typography>
										)}
										<WorkedHint b={b}/>
									</TableCell>
									<TableCell align="right"><Deviation value={b.deviationHours}/></TableCell>
									<TableCell><Consumed b={b}/></TableCell>
									<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{billedLabel(b)}</TableCell>
									<TableCell sx={{whiteSpace: "nowrap"}}><Delivery b={b}/></TableCell>
								</TableRow>
							))}
						</TableBody>
						<TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
							<TableRow>
								<TableCell>Total presupuestos <Typography variant="caption" color="text.secondary" component="span">(sin T&M)</Typography></TableCell>
								<TableCell align="right">{h(plannedTotal)}</TableCell>
								<TableCell align="right">{h(dedicatedTotal)}</TableCell>
								<TableCell align="right"><Deviation value={deviationTotal} bold/></TableCell>
								<TableCell colSpan={3}/>
							</TableRow>
							<TableRow>
								<TableCell colSpan={2}>
									Horas sin asignar de clientes
									{internal > 0 && <Typography variant="caption" color="text.secondary" component="span"> (sin las {h(internal)} h internas de Arteco)</Typography>}
								</TableCell>
								<TableCell align="right" sx={unassigned > 0 ? {color: "error.main !important"} : undefined}>{h(unassigned)}</TableCell>
								<TableCell colSpan={4}/>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			)}
			{compact && (
				<Stack direction="row" justifyContent="space-between" sx={{mt: 2}}>
					<Typography variant="body2" fontWeight={700}>Desvío total</Typography>
					<Deviation value={deviationTotal} bold/>
				</Stack>
			)}
			<Typography variant="caption" color="text.secondary" display="block" sx={{mt: 2}}>
				Consumido son las dedicadas sobre el techo, solo en proyectos y bolsas; es avance en horas, no del alcance. Las cuotas cuentan las horas y lo facturado de su periodo en curso. Si el análisis o la aceptación salen como pendientes es porque faltan en la ficha del presupuesto: pídeselo a tu responsable.
			</Typography>
		</Panel>
	);
};
